import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface CampaignRow {
  id: string;
  name: string;
  status: string;
  contactCount: number | null;
  stepCount: number;
  createdAt: any;
}

const STATUS_STYLES: Record<string, React.CSSProperties> = {
  active:    { background: 'rgba(0,232,122,0.1)', color: 'var(--accent-text)' },
  draft:     { background: 'var(--info-dim)', color: 'var(--info)' },
  completed: { background: 'rgba(85,85,106,0.2)', color: 'var(--text-muted)' },
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="badge" style={STATUS_STYLES[status] ?? STATUS_STYLES.draft}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export function OutreachListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(query(collection(db, 'campaigns'), orderBy('createdAt', 'desc')));
      setCampaigns(snap.docs.map(d => {
        const c = d.data();
        return {
          id:           d.id,
          name:         c.name ?? '',
          status:       c.status ?? 'draft',
          contactCount: c.contactCount ?? null,
          stepCount:    (c.sequenceSteps ?? []).length,
          createdAt:    c.createdAt,
        };
      }));
    } catch (e: any) {
      setError(e.message || 'Failed to load campaigns.');
    } finally {
      setLoading(false);
    }
  };

  const totalActive    = campaigns.filter(c => c.status === 'active').length;
  const totalDraft     = campaigns.filter(c => c.status === 'draft').length;
  const totalCompleted = campaigns.filter(c => c.status === 'completed').length;

  return (
    <Layout>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        <div className="stat-card">
          <div className="stat-label">Total</div>
          <div className="stat-value">{loading ? '—' : campaigns.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value" style={{ color: 'var(--accent-text)' }}>{loading ? '—' : totalActive}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Draft</div>
          <div className="stat-value" style={{ color: 'var(--info)' }}>{loading ? '—' : totalDraft}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Completed</div>
          <div className="stat-value" style={{ color: 'var(--text-muted)' }}>{loading ? '—' : totalCompleted}</div>
        </div>
      </div>

      <div className="page-header">
        <h1 className="page-title">Outreach Campaigns</h1>
        <button className="btn-primary" onClick={() => navigate('/outreach/new')}>
          + New Campaign
        </button>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Loading…</div>
        ) : campaigns.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
            No campaigns yet.{' '}
            <button className="btn-primary" style={{ marginLeft: 12 }} onClick={() => navigate('/outreach/new')}>
              Create the first one
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Contacts</th>
                  <th>Steps</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => (
                  <tr
                    key={c.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/outreach/${c.id}`)}
                  >
                    <td style={{ fontWeight: 500 }}>{c.name}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {c.contactCount !== null ? c.contactCount.toLocaleString() : '—'}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {c.stepCount > 0
                        ? c.stepCount
                        : <span style={{ color: 'var(--text-muted)' }}>none</span>}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{fmtDate(c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
